import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Flag } from "lucide-react";

const REASONS = [
  { v: "adult", label: "🔞 Adult / 18+" },
  { v: "spam", label: "Spam / low quality" },
  { v: "harassment", label: "Harassment / hate" },
  { v: "other", label: "Other" },
];

export default function ReportDialog({ targetType, targetId, testId, compact }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("adult");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        reason,
        note,
      });
      if (data.duplicate) toast.info("You already reported this");
      else toast.success("Thanks — admins have been alerted");
      setOpen(false);
      setNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Report failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        data-testid={testId || `report-${targetType}-${targetId}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        className={`flex items-center gap-1 border border-border px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground hover:border-primary hover:text-primary`}
        title="Report this"
      >
        <Flag className="h-3 w-3" />{!compact && "Report"}
      </button>
      <DialogContent className="max-w-md rounded-none" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Report content</DialogTitle>
          <DialogDescription className="sr-only">
            Pick a reason to report this content to platform admins.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">Reports go straight to platform admins. 🔞 adult flags trigger an immediate alert.</div>
          <div className="space-y-1">
            {REASONS.map((r) => (
              <label key={r.v} className={`flex items-center gap-2 border p-3 cursor-pointer text-sm ${reason === r.v ? "border-primary bg-primary/5" : "border-border"}`}>
                <input
                  type="radio"
                  name="report-reason"
                  data-testid={`report-reason-${r.v}`}
                  checked={reason === r.v}
                  onChange={() => setReason(r.v)}
                  className="accent-primary"
                />
                {r.label}
              </label>
            ))}
          </div>
          <Textarea
            data-testid="report-note"
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional context (max 600 chars)…"
            maxLength={600}
            className="rounded-none bg-input"
          />
        </div>
        <DialogFooter>
          <Button
            data-testid="report-submit"
            onClick={submit}
            disabled={busy}
            className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {busy ? "Sending…" : "Submit report"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
